/* Which chat instances the shell keeps mounted, and which one is on screen.

   2026-11-04: a long job's next part starts from the chat (HomeChat's
   check-in chain), and the chat lived inside the /home route, so leaving for
   Notifications mid-part unmounted it. On return the conversation was rebuilt
   from the server, and a rebuilt check-in card never continues on its own:
   the job sat for 2 min 46 s until Keep going. The decision then was that a
   job carries on while the person is on other pages.

   So the shell renders the /home chat itself and keeps an instance mounted,
   out of sight, while it is busy (a turn or a long job going). An idle chat
   that is not on screen is let go, so everything else behaves as before. Pure
   functions: the component is shell/ChatHost.tsx. */

/** What the /home address asks for (HomeChatRoute in App.tsx). */
export interface ChatRequest {
  /** location.state.resetKey: the sidebar's New thread / resume. */
  resetKey: number;
  startNew: boolean;
  /** The conversation the address or the sidebar names, if any. */
  threadId: string | null;
  draft: string;
}

export interface ChatInstance {
  id: number;
  /** The request it was opened for (kept current while it is on screen). */
  req: ChatRequest;
  /** The conversation it has open, once it says. */
  threadId: string | null;
  busy: boolean;
}

export interface ChatHostState {
  instances: ChatInstance[];
  /** The instance on screen; null when another page is. */
  shown: number | null;
  nextId: number;
}

export type ChatHostEvent =
  | { type: 'route'; req: ChatRequest }
  | { type: 'leave' }
  | { type: 'activity'; id: number; busy: boolean; threadId: string | null }
  | { type: 'thread'; id: number; threadId: string };

export const EMPTY_HOST: ChatHostState = { instances: [], shown: null, nextId: 1 };

/** Instances not on screen are kept only while busy. */
function prune(s: ChatHostState): ChatHostState {
  const instances = s.instances.filter((i) => i.id === s.shown || i.busy);
  return instances.length === s.instances.length ? s : { ...s, instances };
}

function show(s: ChatHostState, id: number, req: ChatRequest): ChatHostState {
  return {
    ...s,
    shown: id,
    instances: s.instances.map((i) => (i.id === id ? { ...i, req } : i)),
  };
}

/** The instance a /home request is for, or null for a new one. */
export function instanceFor(s: ChatHostState, req: ChatRequest): number | null {
  const current = s.instances.find((i) => i.id === s.shown) ?? null;
  // The conversation the address names, already open here: a job still
  // running (the sidebar or a link back to it), or the chat on screen for
  // the same navigation. An idle one reopened from the sidebar (a new
  // resetKey) is opened fresh, as it always was.
  if (req.threadId && !req.draft) {
    const open = s.instances.find((i) => i.threadId === req.threadId);
    if (open && (open.busy || (open === current && open.req.resetKey === req.resetKey))) return open.id;
  }
  // The same navigation as the chat on screen: its own ?t= move, a re-render.
  if (current && current.req.resetKey === req.resetKey && (!req.startNew || current.req.startNew)) {
    return current.id;
  }
  // Plain Home while a job runs out of sight (from another page, or from a
  // New thread opened beside it): that job's conversation is the person's
  // latest, which plain Home opens anyway. A second chat opened on it would
  // offer Keep going for a part its hidden twin has already started. A
  // request with a hand-off draft is for a chat that shows the draft.
  if (!req.threadId && !req.startNew && !req.draft) {
    const running = s.instances.filter((i) => i.busy && i.id !== current?.id);
    if (running.length) return running[running.length - 1].id;
  }
  return null;
}

export function chatHostReducer(s: ChatHostState, e: ChatHostEvent): ChatHostState {
  switch (e.type) {
    case 'route': {
      const id = instanceFor(s, e.req);
      if (id !== null) return prune(show(s, id, e.req));
      const fresh: ChatInstance = { id: s.nextId, req: e.req, threadId: e.req.threadId, busy: false };
      return prune({ instances: [...s.instances, fresh], shown: fresh.id, nextId: s.nextId + 1 });
    }
    case 'leave':
      return prune({ ...s, shown: null });
    case 'thread':
      return {
        ...s,
        instances: s.instances.map((i) => (i.id === e.id ? { ...i, threadId: e.threadId } : i)),
      };
    case 'activity': {
      const instances = s.instances.map((i) =>
        i.id === e.id ? { ...i, busy: e.busy, threadId: e.threadId ?? i.threadId } : i,
      );
      return prune({ ...s, instances });
    }
  }
}
